import { get, post, put } from '@api/api-client';

export type AppUserRole = 'superadmin' | 'admin' | 'editor';

export interface AppUserDto {
  id: string;
  username: string;
  email: string;
  name: string;
  country: string | null;
  role: AppUserRole;
  isActive: boolean;
  hasPassword: boolean;
  mustChangePassword: boolean;
  createdAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  meta: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

interface CreateAppUserRequest {
  username: string;
  email: string;
  name: string;
  country?: string;
  role: AppUserRole;
}

interface UpdateAppUserRequest {
  name?: string;
  email?: string;
  country?: string | null;
  role?: AppUserRole;
  isActive?: boolean;
}

export async function listAppUsers(
  page: number = 1,
  pageSize: number = 20,
  search?: string,
): Promise<PaginatedResponse<AppUserDto>> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (search) {
    params.set('search', search);
  }
  return get<PaginatedResponse<AppUserDto>>(`/app-users?${params.toString()}`);
}

export async function createAppUser(data: CreateAppUserRequest): Promise<AppUserDto> {
  return post<AppUserDto>('/app-users', data);
}

export async function updateAppUser(id: string, data: UpdateAppUserRequest): Promise<AppUserDto> {
  return put<AppUserDto>(`/app-users/${id}`, data);
}

/**
 * Resets a user's password to a generated one and returns it. The only time it
 * exists in the clear, so it is shown once and never fetched again.
 */
export async function resetUserPassword(id: string): Promise<{ password: string }> {
  return post<{ password: string }>(`/app-users/${id}/reset-password`, {});
}

/** Replaces your own password, clearing any pending reset requirement. */
export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ success: boolean }> {
  return post<{ success: boolean }>('/app-users/me/change-password', {
    currentPassword,
    newPassword,
  });
}
